import { useClerk } from '@clerk/clerk-react';
import { AccountProvider } from '@flobi/asset-library/account';
import { safeUrl } from '@flobi/asset-library/notifications';
import '@flobi/asset-library/share.css';
import '@flobi/asset-library/account.css';
import { useCallback, type ComponentProps, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { openArtworkInvite } from '../share/artworkShare';
import { useTheme } from '../theme';
import { ArtworkSettings } from './ArtworkSettings';
import { HOME_URL } from '../homeUrl';
import { clearMediaCache } from '../services/mediaCache';
import { inAppPath } from '../home/notificationLink';

type Provider = ComponentProps<typeof AccountProvider>;

const GATEWAY_URL = import.meta.env.VITE_BACKEND_URL as string | undefined;

/**
 * The one account kit for Artwork (SETTINGS-MODEL): the account menu behind Home's workspace pill, the Settings
 * window over Home and the editor, and Artwork's page in it. It sits inside the notification kit's provider and
 * takes its gateway call, token, workspaces and switch, so the two never disagree about which workspace this is.
 *
 * The theme is the person's: "Everywhere" and "In Artwork" (SETTINGS-MODEL q5) resolve to light or dark here, and
 * Artwork's own palette follows it.
 */
export function ArtworkAccount({
  http,
  getToken,
  workspaces,
  activeWorkspaceId,
  switchWorkspace,
  children,
}: {
  http: NonNullable<Provider['http']>;
  getToken: NonNullable<Provider['getToken']>;
  workspaces: NonNullable<Provider['workspaces']>;
  activeWorkspaceId?: string;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  children: ReactNode;
}) {
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const { theme, setChoice } = useTheme();

  // The workspace lives in the token and everything on screen belongs to the old one: a switch goes to Home.
  const switchAndGoHome = useCallback(
    async (id: string) => {
      await switchWorkspace(id);
      navigate('/', { replace: true });
    },
    [switchWorkspace, navigate],
  );
  // Where the kit sends this tab (a dock drop with nothing open goes to /receive): an Artwork page through the router,
  // in place; anything else by a full load. Checked as a notification row's address is (ArtworkNotifications'
  // `rowTarget`), so an address that is not this app or a Flobi app opens Artwork's start page instead.
  const go = useCallback(
    (url: string) => {
      const href = safeUrl(url, { here: window.location.origin, fallback: '/' });
      const path = inAppPath(href, window.location.origin);
      if (path) navigate(path);
      else window.location.assign(href);
    },
    [navigate],
  );
  const onThemeChange = useCallback(
    (mode: 'light' | 'dark') => {
      if (mode !== theme) setChoice(mode);
    },
    [theme, setChoice],
  );

  return (
    <AccountProvider
      http={http}
      getToken={getToken}
      appSlug="artwork"
      appLabel="Artwork"
      theme="artwork"
      mode={theme}
      homeUrl={HOME_URL}
      mcpBase={GATEWAY_URL}
      workspaces={workspaces}
      activeWorkspaceId={activeWorkspaceId}
      switchWorkspace={switchAndGoHome}
      // The pictures kept in this browser go with the person (`services/mediaCache`).
      signOut={() => void clearMediaCache().finally(() => signOut())}
      // Top up opens the kit's wallet; with the key it adds a card in place, without it Home does.
      stripeKey={import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY}
      openInvite={() => openArtworkInvite()}
      onThemeChange={onThemeChange}
      navigate={go}
    >
      <ArtworkSettings />
      {children}
    </AccountProvider>
  );
}
